import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Clock3, ShieldCheck } from "lucide-react";
import { memo, useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { homeHref } from "@/features/racing/routes";

import { api, type RaceSummary } from "@/api";
import { countdown, EmptyState, ErrorState, money } from "@/components/ui";
import { trackTitle, venueLabel } from "@/features/racing/venue";
import { useRoutePrefetch } from "@/hooks/useRoutePrefetch";
import { useRealtimeTopic } from "@/realtime/RealtimeProvider";

import { HomeDiscovery } from "./HomeSecondary";

function HomePage({
  currentRaceId = null,
  nowMs,
}: {
  currentRaceId?: string | null;
  nowMs?: number;
}) {
  const [localNow, setLocalNow] = useState(() => Date.now());
  const now = nowMs ?? localNow;
  const prefetchRoute = useRoutePrefetch();
  useRealtimeTopic("world.races");
  useRealtimeTopic("world.jackpot");

  useEffect(() => {
    if (nowMs !== undefined) return;
    const timer = window.setInterval(() => setLocalNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [nowMs]);

  const upcoming = useQuery({
    queryKey: ["races", "upcoming", 20],
    queryFn: () => api.upcoming(20),
    refetchInterval: 10_000,
  });
  const prize = useQuery({
    queryKey: ["prizes", "current"],
    queryFn: api.currentPrizePeriod,
    refetchInterval: 30_000,
  });
  const season = useQuery({
    queryKey: ["season", "current"],
    queryFn: api.currentSeason,
    staleTime: 60_000,
  });
  const publishedNext = (upcoming.data?.races ?? []).filter(
    (race) => race.raceId !== currentRaceId && race.status !== "official",
  );
  const period = prize.data?.period as
    { weeklyPrizePoolCents?: unknown } | null | undefined;
  const poolCents = period?.weeklyPrizePoolCents;
  const poolKnown =
    typeof poolCents === "number" && Number.isSafeInteger(poolCents);

  return (
    <div className="home-context">
      <section className="home-section" aria-labelledby="home-next-up">
        <header className="home-section__heading">
          <div>
            <span className="home-eyebrow">The racing continues</span>
            <h2 id="home-next-up">Next up</h2>
          </div>
          <Link className="home-text-link" href="/app/schedule">
            View schedule <ArrowUpRight aria-hidden size={16} />
          </Link>
        </header>
        {upcoming.isPending ? (
          <HomeNextUpSkeleton />
        ) : upcoming.error ? (
          <ErrorState
            error={upcoming.error}
            retry={() => void upcoming.refetch()}
          />
        ) : publishedNext.length ? (
          <div className="home-next-up" aria-label="Upcoming published races">
            {publishedNext.slice(0, 6).map((race) => (
              <NextRaceCard
                key={race.raceId}
                race={race}
                now={now}
                onIntent={() =>
                  prefetchRoute(`/app/?market=${race.raceId}&tab=bet`)
                }
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="You're up to date"
            detail="The next races will appear here as soon as they are published."
          />
        )}
      </section>

      <section className="home-world" aria-label="World at a glance">
        <dl className="home-world__stats">
          <div>
            <dt>Published next</dt>
            <dd>
              {upcoming.error
                ? "Unavailable"
                : upcoming.isPending
                  ? "—"
                  : publishedNext.length}
            </dd>
            <span>Races in the current feed</span>
          </div>
          <div>
            <dt>Weekly prize pool</dt>
            <dd>
              {prize.error ? "Unavailable" : poolKnown ? money(poolCents) : "—"}
            </dd>
            <span>
              {prize.isPending
                ? "Loading published pool"
                : prize.error
                  ? "Pool data could not be loaded"
                  : period === null
                    ? "No open prize period"
                    : poolKnown
                      ? "Accrued so far · not a fixed return"
                      : "Pool not published"}
            </span>
          </div>
          <div>
            <dt>Current season</dt>
            <dd>
              {season.error ? "Unavailable" : (season.data?.season.name ?? "—")}
            </dd>
            <span>
              {season.data && !season.error
                ? `Ends ${new Date(season.data.season.endsAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                : season.error
                  ? "Season data could not be loaded"
                  : "Loading season"}
            </span>
          </div>
        </dl>
        <div className="home-world__note">
          <p>
            Ownership prizes are shared by weighted points. Leaderboard rank
            does not determine payouts.
          </p>
          <Link className="home-text-link" href="/app/verify">
            <ShieldCheck aria-hidden size={16} /> Verify every race
          </Link>
        </div>
      </section>

      <HomeDiscovery now={now} />
    </div>
  );
}

// Keep secondary data/copy on a whole-second cadence, not every race tick.
export default memo(HomePage);

function HomeNextUpSkeleton() {
  return (
    <div
      className="home-next-up"
      role="status"
      aria-label="Loading upcoming races"
    >
      {Array.from({ length: 6 }, (_, index) => (
        <div
          className="home-next-card home-next-card--skeleton"
          key={index}
          aria-hidden
        >
          <span className="skeleton-fill" />
          <strong className="skeleton-fill" />
          <span className="skeleton-fill" />
          <span className="skeleton-fill" />
        </div>
      ))}
    </div>
  );
}

function NextRaceCard({
  race,
  now,
  onIntent,
}: {
  race: RaceSummary;
  now: number;
  onIntent: () => void;
}) {
  const search = useSearch();
  const live = race.status === "running";
  const closed =
    race.status === "locked" ||
    (race.locksAt && Date.parse(race.locksAt) <= now);
  const openingLater =
    race.bettingOpensAt && Date.parse(race.bettingOpensAt) > now;
  const status = live
    ? "On track"
    : closed
      ? "Betting closed"
      : race.status === "betting" && !openingLater
        ? "Betting open"
        : "Scheduled";
  const raceLabel = race.raceNumber
    ? /^race\b/i.test(race.raceNumber)
      ? race.raceNumber
      : `Race ${race.raceNumber}`
    : "Upcoming race";

  return (
    <Link
      className="home-next-card"
      href={homeHref({ market: race.raceId, tab: "bet" }, search)}
      aria-label={`Open ${trackTitle(race)}, ${raceLabel}, ${race.distanceM} metres`}
      onFocus={onIntent}
      onMouseEnter={onIntent}
    >
      <div className="home-next-card__top">
        <span>{trackTitle(race)}</span>
        <ArrowUpRight aria-hidden size={15} />
      </div>
      <strong>
        {raceLabel} <span>{race.distanceM}m</span>
      </strong>
      <p>
        {race.fieldSize} runners
        {venueLabel(race) ? ` · ${venueLabel(race)}` : ""}
      </p>
      <div className="home-next-card__bottom">
        <span className={status === "Betting open" ? "is-open" : undefined}>
          {status}
        </span>
        <span className="home-next-card__countdown">
          <Clock3 aria-hidden size={12} />
          {live
            ? "Live"
            : Date.parse(race.scheduledAt) <= now
              ? "Awaiting start"
              : countdown(race.scheduledAt, now)}
        </span>
      </div>
      <time dateTime={race.scheduledAt}>
        Scheduled{" "}
        {new Date(race.scheduledAt).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </time>
    </Link>
  );
}
